"use client";

import { useRef, useState, useEffect, useCallback } from "react";
import Image from "next/image";
import { motion, AnimatePresence } from "framer-motion";
import { parseLrc, parsePlainLyrics, getCurrentLine, type LrcLine } from "@/lib/lrc-parser";
import { useI18n } from "@/i18n/I18nProvider";
import { useMusicPlayer } from "@/context/MusicPlayerContext";
import type { RepeatMode } from "@/context/MusicPlayerContext";

interface MusicPlayerProps {
  avatarUrl: string | null;
  avatarAlt: string;
  isExpanded?: boolean;
  onToggleExpand?: () => void;
}

function formatTime(sec: number): string {
  if (!Number.isFinite(sec) || sec < 0) return "0:00";
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

function cycleRepeat(mode: RepeatMode): RepeatMode {
  if (mode === "off") return "one";
  if (mode === "one") return "all";
  return "off";
}

export function MusicPlayer({ avatarUrl, avatarAlt, isExpanded = false, onToggleExpand }: MusicPlayerProps) {
  const lyricsRef = useRef<HTMLDivElement>(null);
  const progressBarRef = useRef<HTMLDivElement>(null);
  const player = useMusicPlayer();
  const { t } = useI18n();

  const {
    songs,
    loading,
    currentIndex,
    currentSong,
    isPlaying,
    currentTime,
    duration,
    volume,
    repeatMode,
    shuffle,
    setVolume,
    setRepeatMode,
    setShuffle,
    togglePlay,
    goNext,
    goPrev,
    selectSong,
    seekToPercent,
    seekFromEvent,
    discImageSrc: ctxDiscImageSrc,
    discImageAlt: ctxDiscImageAlt,
  } = player;

  const discImageSrc = ctxDiscImageSrc || avatarUrl || "";
  const discImageAlt = ctxDiscImageAlt || avatarAlt;

  const lrcLines: LrcLine[] = currentSong?.lyrics ? parseLrc(currentSong.lyrics) : [];
  const plainLines = currentSong?.lyrics ? parsePlainLyrics(currentSong.lyrics) : [];
  const hasLrc = lrcLines.length > 0;
  const hasPlainLyrics = !hasLrc && plainLines.length > 0;
  const { index: currentLineIndex } = getCurrentLine(lrcLines, currentTime);

  const scrollRef = useRef<HTMLDivElement>(null);

  const seek = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => seekFromEvent(e),
    [seekFromEvent]
  );

  const [isSeeking, setIsSeeking] = useState(false);
  const getPercentFromEvent = useCallback((clientX: number) => {
    if (!progressBarRef.current) return 0;
    const rect = progressBarRef.current.getBoundingClientRect();
    return (clientX - rect.left) / rect.width;
  }, []);

  const handleThumbMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      if (!duration) return;
      setIsSeeking(true);
      seekToPercent(getPercentFromEvent(e.clientX));
    },
    [duration, seekToPercent, getPercentFromEvent]
  );

  const handleThumbTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (!duration) return;
      setIsSeeking(true);
      seekToPercent(getPercentFromEvent(e.touches[0].clientX));
    },
    [duration, seekToPercent, getPercentFromEvent]
  );

  useEffect(() => {
    if (!isSeeking) return;
    const onMove = (e: MouseEvent) => seekToPercent(getPercentFromEvent(e.clientX));
    const onTouchMove = (e: TouchEvent) => {
      e.preventDefault();
      seekToPercent(getPercentFromEvent(e.touches[0].clientX));
    };
    const onEnd = () => setIsSeeking(false);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onEnd);
    window.addEventListener("touchmove", onTouchMove, { passive: false });
    window.addEventListener("touchend", onEnd);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onEnd);
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("touchend", onEnd);
    };
  }, [isSeeking, seekToPercent, getPercentFromEvent]);

  useEffect(() => {
    if (currentLineIndex < 0 || !lyricsRef.current) return;
    const active = lyricsRef.current.querySelector("[data-active]");
    active?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [currentLineIndex]);

  if (loading && songs.length === 0) {
    return (
      <motion.div
        ref={scrollRef}
        className="flex-shrink-0 relative z-10"
        initial={{ opacity: 0, scale: 0.7 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.3, duration: 0.9 }}
      >
        <div className="avatar-ring h-48 w-48 overflow-hidden rounded-full sm:h-56 sm:w-56 lg:h-64 lg:w-64 bg-dev-text-muted/20 animate-pulse" />
      </motion.div>
    );
  }

  if (songs.length === 0 && avatarUrl) {
    return (
      <motion.div
        ref={scrollRef}
        className="flex-shrink-0 relative z-10"
        style={{ transformPerspective: 1200 }}
        initial={{ opacity: 0, scale: 0.7, rotateZ: -8 }}
        animate={{ opacity: 1, scale: 1, rotateZ: 0 }}
        transition={{ delay: 0.3, duration: 0.9, ease: [0.22, 1, 0.36, 1] as const }}
      >
        <div className="avatar-ring h-48 w-48 overflow-hidden rounded-full sm:h-56 sm:w-56 lg:h-64 lg:w-64">
          <Image
            src={avatarUrl}
            alt={avatarAlt}
            width={256}
            height={256}
            className="h-full w-full object-cover"
            priority
          />
        </div>
      </motion.div>
    );
  }

  if (songs.length === 0) return null;

  const playerBlock = (
    <>
      <div className="flex flex-col items-center gap-6 flex-shrink-0">
        {/* Turntable — máy đĩa than */}
        <div className="turntable-perspective-wrap">
          <div
            className={`turntable-unit h-52 w-52 sm:h-60 sm:w-60 lg:h-72 lg:w-72 flex-shrink-0 ${isPlaying ? "turntable-unit-playing" : ""}`}
          >
          <div className="turntable-base h-full w-full">
            <div
              className={`turntable-led ${isPlaying ? "turntable-led-on" : "turntable-led-off"}`}
              aria-hidden
            />
            <div className="turntable-tonearm-pillar" aria-hidden />
            <svg
              className={`turntable-tonearm ${isPlaying ? "turntable-tonearm-playing" : "turntable-tonearm-rest"}`}
              viewBox="0 0 100 32"
              fill="none"
              aria-hidden
            >
              <defs>
                <linearGradient id="tonearm-shine" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="rgba(255,255,255,0.06)" />
                  <stop offset="50%" stopColor="rgba(255,255,255,0.12)" />
                  <stop offset="100%" stopColor="rgba(255,255,255,0.02)" />
                </linearGradient>
              </defs>
              <path
                d="M 3 16 L 92 16"
                stroke="url(#tonearm-shine)"
                strokeWidth="2.5"
                strokeLinecap="round"
              />
              <path
                d="M 3 16 L 92 16"
                stroke="currentColor"
                strokeWidth="1.2"
                strokeLinecap="round"
                className="text-dev-text-muted/50"
              />
              <ellipse cx="95" cy="16" rx="6" ry="5" fill="#1e2128" stroke="rgba(255,255,255,0.05)" strokeWidth="1" />
              <circle cx="3" cy="16" r="3.5" fill="#1a1d24" stroke="rgba(255,255,255,0.06)" strokeWidth="1" />
            </svg>
            <div className="turntable-volume-panel">
              <div
                className="turntable-volume-knob"
                style={{ transform: `rotate(${-45 + volume * 90}deg)` }}
                aria-hidden
              />
              <input
                type="range"
                min="0"
                max="1"
                step="0.05"
                value={volume}
                onChange={(e) => setVolume(parseFloat(e.target.value))}
                className="turntable-volume-input"
                aria-label={t("home.musicPlayer.volume")}
              />
              <span className="turntable-volume-label">VOL</span>
            </div>
            <div className="turntable-progress-area">
              <span className="turntable-time-display" aria-live="polite">
                {formatTime(currentTime)}
              </span>
              <div
                ref={progressBarRef}
                className="turntable-progress-bar"
                onClick={seek}
                role="progressbar"
                aria-valuenow={Math.round(currentTime)}
                aria-valuemin={0}
                aria-valuemax={Math.round(duration || 0)}
                aria-label={t("home.musicPlayer.progress")}
              >
                <div
                  className="turntable-progress-fill"
                  style={{ width: `${duration ? (currentTime / duration) * 100 : 0}%` }}
                />
                <div
                  className="turntable-progress-thumb"
                  style={{
                    left: `${duration ? (currentTime / duration) * 100 : 0}%`,
                  }}
                  onMouseDown={handleThumbMouseDown}
                  onTouchStart={handleThumbTouchStart}
                  onClick={(e) => e.stopPropagation()}
                  role="slider"
                  aria-valuenow={Math.round(currentTime)}
                  aria-valuemin={0}
                  aria-valuemax={Math.round(duration || 0)}
                  aria-label={t("home.musicPlayer.progress")}
                  tabIndex={0}
                />
              </div>
              <span className="turntable-time-display" aria-live="polite">
                {formatTime(duration || 0)}
              </span>
            </div>
            <div className="turntable-platter">
              <div className="turntable-platter-mat" aria-hidden />
              <div className="turntable-spindle" aria-hidden />
              <div className="vinyl-wrapper" style={{ pointerEvents: "none" }}>
                <div className={`vinyl-disc ${isPlaying ? "vinyl-disc-spin" : ""}`}>
                  <div className="vinyl-ring">
                    <div className="vinyl-label">
                      {discImageSrc ? (
                        <Image
                          src={discImageSrc}
                          alt={discImageAlt}
                          width={256}
                          height={256}
                          className="h-full w-full object-cover"
                          unoptimized
                        />
                      ) : (
                        <div className="h-full w-full bg-dev-text-muted/20 flex items-center justify-center text-4xl">
                          🎵
                        </div>
                      )}
                    </div>
                    <div className="vinyl-center-hole" aria-hidden />
                  </div>
                </div>
              </div>
            </div>
          </div>
          </div>
        </div>

        {/* Controls & info */}
        <div className="w-full max-w-[260px] space-y-4">
          {!isExpanded && (
            <div className="text-center min-h-[2.5rem] px-2">
              <p className="font-semibold text-dev-text-primary truncate text-base">{currentSong?.title ?? "—"}</p>
              <p className="text-xs text-dev-text-muted truncate mt-0.5">{currentSong?.artist ?? ""}</p>
            </div>
          )}
          <div className="flex items-center justify-center gap-1 sm:gap-2">
            <button
              onClick={() => setShuffle(!shuffle)}
              className={`p-2 rounded-full transition-all duration-200 hover:scale-105 ${
                shuffle ? "text-dev-cyan" : "text-dev-text-muted hover:text-dev-cyan hover:bg-dev-cyan/10"
              }`}
              aria-label={t("home.musicPlayer.shuffle")}
              title={t("home.musicPlayer.shuffle")}
            >
              <svg className="h-4 w-4 sm:h-5 sm:w-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M10.59 9.17L5.41 4 4 5.41l5.17 5.17 1.42-1.41zM14.5 4l2.04 2.04-4.96 4.96-2.04-2.04L14.5 4zm3.33 9.5l-1.41 1.41 3.13 3.13L14.5 20H20v-5.5l-2.04 2.04-3.13-3.13 1.41-1.41zM9.36 14.5l1.41-1.41-3.13-3.13L9.5 4H4v5.5l2.04-2.04 3.13 3.13z" />
              </svg>
            </button>
            <button
              onClick={goPrev}
              className="p-2.5 rounded-full text-dev-text-muted hover:text-dev-cyan hover:bg-dev-cyan/10 transition-all duration-200 hover:scale-105"
              aria-label={t("home.musicPlayer.prev")}
            >
              <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M6 6h2v12H6zm3.5 6l8.5 6V6z" />
              </svg>
            </button>
            <button
              onClick={togglePlay}
              className="p-4 rounded-full bg-gradient-to-br from-dev-cyan/25 to-dev-blue/25 text-dev-cyan hover:from-dev-cyan/35 hover:to-dev-blue/35 transition-all duration-200 hover:scale-105 shadow-[0_0_20px_rgba(6,182,212,0.2)]"
              aria-label={isPlaying ? t("home.musicPlayer.pause") : t("home.musicPlayer.play")}
            >
              {isPlaying ? (
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
                </svg>
              ) : (
                <svg className="h-5 w-5 ml-0.5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M8 5v14l11-7z" />
                </svg>
              )}
            </button>
            <button
              onClick={goNext}
              className="p-2.5 rounded-full text-dev-text-muted hover:text-dev-cyan hover:bg-dev-cyan/10 transition-all duration-200 hover:scale-105"
              aria-label={t("home.musicPlayer.next")}
            >
              <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M6 18l8.5-6L6 6v12zm2-10v8l5.4-4-5.4-4zm13 4V6h-2v6h2z" />
              </svg>
            </button>
            <button
              onClick={() => setRepeatMode(cycleRepeat(repeatMode))}
              className={`relative p-2 rounded-full transition-all duration-200 hover:scale-105 ${
                repeatMode !== "off" ? "text-dev-cyan" : "text-dev-text-muted hover:text-dev-cyan hover:bg-dev-cyan/10"
              }`}
              aria-label={t(`home.musicPlayer.repeat.${repeatMode}`)}
              title={t(`home.musicPlayer.repeat.${repeatMode}`)}
            >
              <svg className="h-4 w-4 sm:h-5 sm:w-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M7 7h10v3l4-4-4-4v3H5v6h2V7zm10 10H7v-3l-4 4 4 4v-3h12v-6h-2v4z" />
              </svg>
              {repeatMode === "one" && (
                <span className="absolute -bottom-0.5 -right-0.5 flex h-3 w-3 items-center justify-center rounded bg-dev-cyan text-[8px] font-bold text-white leading-none">
                  1
                </span>
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );

  const lyricsBlock = (
    <motion.div
      ref={lyricsRef}
      initial={isExpanded ? { opacity: 0, x: -12 } : false}
      animate={isExpanded ? { opacity: 1, x: 0 } : false}
      transition={{ delay: 0.1, duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
      className="w-full min-w-[180px] flex-1 max-w-[320px] max-h-80 overflow-y-auto rounded-xl bg-dev-text-muted/[0.07] backdrop-blur-sm border border-dev-text-muted/15 p-5 scrollbar-thin shadow-inner min-h-[140px]"
    >
      <p className="text-xs font-semibold text-dev-cyan/80 uppercase tracking-widest mb-4">
        {t("home.musicPlayer.lyricsTitle")}
      </p>
      {hasLrc ? (
        lrcLines.map((line, i) => (
          <p
            key={i}
            data-active={i === currentLineIndex ? true : undefined}
            className={`py-2.5 text-sm leading-relaxed transition-colors duration-200 ${
              i === currentLineIndex ? "text-dev-cyan font-medium" : "text-dev-text-muted"
            }`}
          >
            {line.text}
          </p>
        ))
      ) : hasPlainLyrics ? (
        plainLines.map((line, i) => (
          <p key={i} className="py-2.5 text-sm leading-relaxed text-dev-text-muted">
            {line}
          </p>
        ))
      ) : (
        <p className="py-4 text-sm text-dev-text-muted/70 italic">{t("home.musicPlayer.noLyrics")}</p>
      )}
    </motion.div>
  );

  const playlistBlock = (
    <motion.div
      initial={isExpanded ? { opacity: 0, x: 12 } : false}
      animate={isExpanded ? { opacity: 1, x: 0 } : false}
      transition={{ delay: 0.15, duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
      className="w-full min-w-[160px] max-w-[280px] flex-shrink-0"
    >
      <p className="text-xs font-semibold text-dev-cyan/80 uppercase tracking-widest mb-3">
        {t("home.musicPlayer.showPlaylist")}
      </p>
      <ul className="space-y-2 max-h-64 overflow-y-auto rounded-xl bg-dev-text-muted/[0.05] border border-dev-text-muted/10 p-3">
        {songs.map((song, i) => (
          <li key={song.id}>
            <button
              onClick={() => selectSong(i)}
              className={`w-full text-left px-3 py-2.5 rounded-lg text-sm transition-colors ${
                i === currentIndex
                  ? "bg-dev-cyan/20 text-dev-cyan"
                  : "text-dev-text-secondary hover:bg-dev-text-muted/10"
              }`}
            >
              <span className="truncate block">{song.title}</span>
              {song.artist && (
                <span className="text-xs text-dev-text-muted truncate block">{song.artist}</span>
              )}
            </button>
          </li>
        ))}
      </ul>
    </motion.div>
  );

  const easeSmooth = [0.22, 1, 0.36, 1] as const;

  return (
    <motion.div
      ref={scrollRef}
      className={`flex flex-col items-center gap-5 relative z-10 music-player-scroll-isolate ${
        isExpanded ? "w-full max-w-full" : "flex-shrink-0 md:items-stretch"
      }`}
      initial={{ opacity: 0, scale: 0.7, rotateZ: -8 }}
      animate={{ opacity: 1, scale: 1, rotateZ: 0 }}
      transition={{ delay: 0.3, duration: 0.9, ease: easeSmooth }}
    >
      <AnimatePresence mode="wait">
        {isExpanded ? (
          <motion.div
            key="expanded"
            initial={{ opacity: 0, x: 56 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 56, transition: { duration: 0.3, ease: easeSmooth } }}
            transition={{ duration: 0.4, ease: easeSmooth }}
            className="flex flex-col gap-6 w-full"
          >
            <motion.div
              className="flex flex-row gap-8 lg:gap-12 items-stretch justify-center flex-wrap"
            >
              {playerBlock}
              {lyricsBlock}
              {playlistBlock}
            </motion.div>
            {onToggleExpand && (
              <motion.button
                onClick={onToggleExpand}
                className="self-center py-2.5 px-5 text-sm text-dev-text-muted hover:text-dev-cyan transition-colors rounded-lg hover:bg-dev-cyan/5"
              >
                {t("home.musicPlayer.collapse")}
              </motion.button>
            )}
          </motion.div>
        ) : (
          <motion.div
            key="collapsed"
            initial={{ opacity: 0, x: -32 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -32, transition: { duration: 0.25, ease: easeSmooth } }}
            transition={{ duration: 0.35, ease: easeSmooth }}
            className="flex flex-col items-center gap-5"
          >
            {playerBlock}
            {onToggleExpand && (
              <motion.button
                onClick={onToggleExpand}
                className="w-full max-w-[260px] py-2.5 text-sm text-dev-cyan/90 hover:text-dev-cyan transition-colors flex items-center justify-center gap-2 rounded-lg bg-dev-cyan/10 hover:bg-dev-cyan/15 border border-dev-cyan/30"
              >
                {t("home.musicPlayer.expand")}
              </motion.button>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
